/**
 * Интервальное повторение Memory Loop.
 *
 * Интервалы адаптируются к ошибкам и скорости восстановления, а не одинаковы
 * для всех (ТЗ §7.4). Ошибка возвращает единицу на шаг назад, а не обнуляет
 * прогресс: полный сброс демотивирует и удлиняет обучение.
 */

import type { MasteryStat } from '@type2x/shared-contracts';

import type { LearningConfig } from './config/priority.v1.js';
import { clamp01 } from './normalize.js';

const MS_PER_HOUR = 3_600_000;

/** Насколько интервал растягивается при уверенном выполнении. */
const EASE_BONUS = 1.3;

/** Насколько интервал сжимается при ошибках. */
const STRUGGLE_PENALTY = 0.5;

/** Индекс ступени лестницы повторений по числу успехов подряд. */
export function ladderStep(consecutiveSuccesses: number, config: LearningConfig): number {
  const lastIndex = config.reviewLadderHours.length - 1;
  return Math.min(Math.max(consecutiveSuccesses, 0), lastIndex);
}

/**
 * Вычисляет момент следующего повторения.
 *
 * @param stat состояние единицы после обновления;
 * @param config конфигурация;
 * @param nowMs текущий момент в миллисекундах календарного времени.
 * @returns календарная метка следующего повторения (ISO 8601).
 */
export function scheduleNextReview(
  stat: MasteryStat,
  config: LearningConfig,
  nowMs: number,
): string {
  const step = ladderStep(stat.consecutiveSuccesses, config);
  const baseHours = config.reviewLadderHours[step] ?? config.reviewLadderHours[0] ?? 1;

  // Уверенное выполнение растягивает интервал, ошибки — сжимают.
  const confidence = clamp01(stat.masteryScore);
  const struggle = clamp01(stat.errorRate + stat.correctionRate * 0.5);
  const factor = 1 + confidence * (EASE_BONUS - 1) - struggle * (1 - STRUGGLE_PENALTY);

  const hours = Math.max(baseHours * factor, config.reviewLadderHours[0] ?? 0.05);
  return new Date(nowMs + hours * MS_PER_HOUR).toISOString();
}

/** Наступил ли срок повторения. */
export function isDue(stat: MasteryStat, nowMs: number): boolean {
  return Date.parse(stat.nextReviewAt) <= nowMs;
}

/**
 * Насколько просрочено повторение, 0..1.
 *
 * Ноль — срок ещё не наступил. Единица — просрочено на неделю и больше:
 * дальше расти нечему, иначе давно забытый материал вытеснит всё остальное.
 */
export function overdueScore(stat: MasteryStat, nowMs: number): number {
  const due = Date.parse(stat.nextReviewAt);
  if (Number.isNaN(due) || due > nowMs) return 0;
  const overdueHours = (nowMs - due) / MS_PER_HOUR;
  return clamp01(overdueHours / 168);
}
